/**
 * The fixed tables of the card in further languages (F28), from household-kit (`lang` and `food`,
 * checked copies in packages/shared): the languages, `webLangOf`, the words of a card, the base
 * vocabulary with the drinks' words and the additives as a default, the official names of the 14
 * allergens. Here only what is KellerLog's own: the words of its page on the net. Changes to the
 * tables go to household-kit, then `npm run kit:lang` and `npm run kit:food`.
 */
import { allergenName, allergenShort } from "@household-kit/food";
import { BASE_PHRASES, CARD_WORDS, DRINK_PHRASES, LANG_LABELS, LANG_NATIVE, LANG_SHORT, LANGS, basePhrase, isLang, phraseKey, webLangOf as kitWebLangOf } from "@household-kit/lang";
import type { Lang } from "@household-kit/lang";

export const CARD_LANGS = LANGS;
export type CardLang = Lang;
export const isCardLang = isLang;
export const CARD_LANG_LABELS = LANG_LABELS;
/** The language's own name and short form, for the switch on the page. */
export const CARD_LANG_NATIVE: Record<CardLang | "de", string> = { de: "Deutsch", ...LANG_NATIVE };
export const CARD_LANG_SHORT = LANG_SHORT;
export { phraseKey };

/**
 * The language of the page: `lang` of the link decides when it is there (one not offered: German);
 * without it the first of the browser's languages the card has (`Accept-Language`), else German.
 */
export const webLangOf = (asked: string | undefined, accept: string | undefined, offered: readonly CardLang[]): CardLang | "de" => kitWebLangOf(asked, accept, offered, { strict: true });

/** The words of the page besides the house's own, by language. */
export interface WebWords {
  wineList: string;
  drinksList: string;
  /** The label of the bar with the parts. */
  parts: string;
  prices: string;
  vintages: string;
  asOf: string;
  askAllergens: string;
  /** In the facts of a drink, lower case: „enthält Sulfite“. */
  contains: string;
  /** Under a part's title: „Enthält Sulfite“. */
  Contains: string;
  organic: string;
  imprint: string;
  empty: string;
  noCard: [string, string];
  offline: [string, string];
}

/** KellerLog's own words of the page; the foot, „Stand“ and „Impressum“ come from household-kit. */
const OWN_WORDS: Record<CardLang | "de", Omit<WebWords, "prices" | "asOf" | "imprint">> = {
  de: {
    wineList: "Weinkarte",
    drinksList: "Getränkekarte",
    parts: "Teile der Karte",
    vintages: "Jahrgänge können sich ändern",
    askAllergens: "Allergene erfragen Sie bitte beim Personal.",
    contains: "enthält",
    Contains: "Enthält",
    organic: "Bio",
    empty: "Zurzeit stehen keine Getränke auf der Karte.",
    noCard: ["Noch keine Karte", "Die Karte ist noch nicht veröffentlicht. Bitte fragen Sie das Personal."],
    offline: ["Zurzeit nicht verfügbar", "Die Karte ist zurzeit nicht verfügbar. Bitte fragen Sie das Personal."],
  },
  en: {
    wineList: "Wine list",
    drinksList: "Drinks",
    parts: "Parts of the menu",
    vintages: "Vintages may change",
    askAllergens: "Please ask our staff about allergens.",
    contains: "contains",
    Contains: "Contains",
    organic: "organic",
    empty: "There are no drinks on the menu at the moment.",
    noCard: ["No menu yet", "The menu has not been published yet. Please ask our staff."],
    offline: ["Currently unavailable", "The menu is currently unavailable. Please ask our staff."],
  },
  es: {
    wineList: "Carta de vinos",
    drinksList: "Bebidas",
    parts: "Partes de la carta",
    vintages: "Las añadas pueden cambiar",
    askAllergens: "Consulte los alérgenos a nuestro personal.",
    contains: "contiene",
    Contains: "Contiene",
    organic: "ecológico",
    empty: "De momento no hay bebidas en la carta.",
    noCard: ["Todavía no hay carta", "La carta aún no está publicada. Pregunte a nuestro personal."],
    offline: ["No disponible en este momento", "La carta no está disponible en este momento. Pregunte a nuestro personal."],
  },
  fr: {
    wineList: "Carte des vins",
    drinksList: "Boissons",
    parts: "Parties de la carte",
    vintages: "Les millésimes peuvent changer",
    askAllergens: "Pour les allergènes, adressez-vous à notre personnel.",
    contains: "contient",
    Contains: "Contient",
    organic: "bio",
    empty: "Aucune boisson sur la carte pour le moment.",
    noCard: ["Pas encore de carte", "La carte n’est pas encore publiée. Veuillez vous adresser à notre personnel."],
    offline: ["Momentanément indisponible", "La carte est momentanément indisponible. Veuillez vous adresser à notre personnel."],
  },
  it: {
    wineList: "Carta dei vini",
    drinksList: "Bevande",
    parts: "Parti della carta",
    vintages: "Le annate possono variare",
    askAllergens: "Per gli allergeni rivolgersi al personale.",
    contains: "contiene",
    Contains: "Contiene",
    organic: "biologico",
    empty: "Al momento non ci sono bevande nella carta.",
    noCard: ["Ancora nessuna carta", "La carta non è ancora pubblicata. Chiedete al personale."],
    offline: ["Al momento non disponibile", "La carta al momento non è disponibile. Chiedete al personale."],
  },
  nl: {
    wineList: "Wijnkaart",
    drinksList: "Drankenkaart",
    parts: "Delen van de kaart",
    vintages: "Jaargangen kunnen wijzigen",
    askAllergens: "Vraag ons personeel naar allergenen.",
    contains: "bevat",
    Contains: "Bevat",
    organic: "biologisch",
    empty: "Er staan op dit moment geen dranken op de kaart.",
    noCard: ["Nog geen kaart", "De kaart is nog niet gepubliceerd. Vraag het ons personeel."],
    offline: ["Momenteel niet beschikbaar", "De kaart is momenteel niet beschikbaar. Vraag het ons personeel."],
  },
  pt: {
    wineList: "Carta de vinhos",
    drinksList: "Bebidas",
    parts: "Partes da carta",
    vintages: "As colheitas podem mudar",
    askAllergens: "Para alergénios, pergunte ao nosso pessoal.",
    contains: "contém",
    Contains: "Contém",
    organic: "biológico",
    empty: "De momento não há bebidas na carta.",
    noCard: ["Ainda sem carta", "A carta ainda não foi publicada. Pergunte ao nosso pessoal."],
    offline: ["De momento indisponível", "A carta está de momento indisponível. Pergunte ao nosso pessoal."],
  },
  tr: {
    wineList: "Şarap listesi",
    drinksList: "İçecekler",
    parts: "Menünün bölümleri",
    vintages: "Rekolteler değişebilir",
    askAllergens: "Alerjenler için lütfen personelimize danışın.",
    contains: "içerir",
    Contains: "İçerir",
    organic: "organik",
    empty: "Şu anda menüde içecek bulunmuyor.",
    noCard: ["Henüz menü yok", "Menü henüz yayımlanmadı. Lütfen personelimize sorun."],
    offline: ["Şu anda kullanılamıyor", "Menü şu anda kullanılamıyor. Lütfen personelimize sorun."],
  },
};


export const WEB_WORDS: Record<CardLang | "de", WebWords> = Object.fromEntries(
  (["de", ...CARD_LANGS] as const).map((l) => [
    l,
    // The foot stands before „· Jahrgänge …“: without the full stop the kit's sentence has (not the one of „MwSt.“).
    { ...OWN_WORDS[l], prices: l === "de" ? CARD_WORDS.de.footer : CARD_WORDS[l].footer.replace(/\.$/, ""), asOf: CARD_WORDS[l].asOf, imprint: CARD_WORDS[l].imprint },
  ]),
) as Record<CardLang | "de", WebWords>;

/** The words of the allergen filter (F29), from household-kit (H16), in the language of the page. */
export interface FilterWords {
  allergenFilter: string;
  allergenFilterLead: string;
  allergenFilterOn: string;
  allergenFilterReset: string;
  allergensUnknown: string;
  /** The line under the boxes, about drinks (`allergenFilterNoteDrinks`). */
  note: string;
}

export function filterWords(lang: CardLang | "de"): FilterWords {
  const w = CARD_WORDS[lang];
  return { allergenFilter: w.allergenFilter, allergenFilterLead: w.allergenFilterLead, allergenFilterOn: w.allergenFilterOn, allergenFilterReset: w.allergenFilterReset, allergensUnknown: w.allergensUnknown, note: w.allergenFilterNoteDrinks };
}

/** The short name of one of the 14 for a box of the filter („Gluten“, „Sulphites“), by its id. */
export const allergenShortIn = (id: string, lang: CardLang | "de"): string | undefined => allergenShort(id, lang);

/** The official name of one of the 14 allergens (annex II LMIV, EUR-Lex), fixed: no house changes it. */
export const officialAllergenIn = (id: string, lang: CardLang): string | undefined => allergenName(id, lang);

/**
 * The base vocabulary of a drinks card: the common words and the additives (a default) with the words
 * clear only on a drinks card („Weizen“, „Halbe“, „Dunkel“), which household-kit keeps apart.
 */
export const BASE_CARD_PHRASES = { ...BASE_PHRASES, ...DRINK_PHRASES };

/** The base vocabulary's word for a text on KellerLog's card: always a drinks card. */
export const baseCardPhrase = (text: string, lang: CardLang): string | undefined => basePhrase(text, lang, { drinks: true });
